import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calculator, AlertCircle } from "lucide-react";
import { useCalculations } from "@/hooks/useCalculations";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface CalculationSelectorProps {
  templateId: number;
  selectedType?: string;
  config?: any;
  onTypeChange: (type: string) => void;
  onConfigChange: (config: any) => void;
}

export function CalculationSelector({
  templateId,
  selectedType,
  config,
  onTypeChange,
  onConfigChange,
}: CalculationSelectorProps) {
  const { calculations, isLoading } = useCalculations(templateId);
  const [selectedCalculationId, setSelectedCalculationId] = useState<string>(
    config?.calculation_id || ''
  );

  // Sincronizar con el prop config cuando cambia
  useEffect(() => {
    if (config?.calculation_id) {
      setSelectedCalculationId(config.calculation_id.toString());
    }
  }, [config]);

  const handleCalculationChange = (calculationId: string) => {
    setSelectedCalculationId(calculationId);

    // Establecer el tipo como el ID del cálculo
    onTypeChange(calculationId);

    // Guardar SOLO la referencia al cálculo (calculation_id)
    // El backend resolverá el cálculo completo en tiempo de render
    onConfigChange({
      calculation_id: parseInt(calculationId),
    });
  };

  const selectedCalculation = calculations?.find(
    c => c.id.toString() === selectedCalculationId
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Cargando cálculos...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!calculations || calculations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Seleccionar Cálculo
          </CardTitle>
          <CardDescription>
            Usa un cálculo previamente configurado
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex flex-col gap-3">
              <p>No hay cálculos configurados para esta plantilla.</p>
              <p>Primero debes crear cálculos en la pestaña "Cálculos".</p>
              <Link to={`/admin/templates_v2/${templateId}`}>
                <Button variant="outline" size="sm">
                  Ir a Cálculos
                </Button>
              </Link>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Seleccionar Cálculo
        </CardTitle>
        <CardDescription>
          Usa un cálculo previamente configurado en la pestaña de Cálculos
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Calculation Selector */}
        <div className="space-y-2">
          <Label>Cálculo</Label>
          <Select value={selectedCalculationId} onValueChange={handleCalculationChange}>
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar cálculo" />
            </SelectTrigger>
            <SelectContent>
              {calculations.map((calc) => (
                <SelectItem key={calc.id} value={calc.id.toString()}>
                  <div className="flex flex-col">
                    <span className="font-medium">{calc.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {calc.calculation_type === 'aggregate' ? 'Agregación' : 'Fórmula'} •
                      {' '}{calc.config?.operation || 'N/A'}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Selected Calculation Details */}
        {selectedCalculation && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
            <div>
              <span className="font-medium text-blue-900">Detalles del Cálculo:</span>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  {selectedCalculation.calculation_type === 'aggregate' ? 'Agregación' : 'Fórmula'}
                </Badge>
                <Badge>
                  {selectedCalculation.config?.operation || 'N/A'}
                </Badge>
              </div>

              {selectedCalculation.config?.scope && (
                <div className="text-blue-800">
                  <strong>Ámbito:</strong>{' '}
                  {selectedCalculation.config.scope === 'main' && 'Formulario Principal'}
                  {selectedCalculation.config.scope === 'referral' && 'Referidos'}
                  {selectedCalculation.config.scope === 'comparison' && 'Comparación'}
                </div>
              )}

              {selectedCalculation.config?.group_name && (
                <div className="text-blue-800">
                  <strong>Grupo:</strong> {selectedCalculation.config.group_name}
                </div>
              )}

              {selectedCalculation.config?.question_name && (
                <div className="text-blue-800">
                  <strong>Pregunta:</strong> {selectedCalculation.config.question_name}
                </div>
              )}

              {selectedCalculation.config?.matrix_config?.row_id && (
                <div className="text-blue-800">
                  <strong>Fila de Matriz:</strong> {selectedCalculation.config.matrix_config.row_id}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hint */}
        <div className="text-xs text-muted-foreground">
          💡 Puedes crear más cálculos en la pestaña "Cálculos" de esta plantilla
        </div>
      </CardContent>
    </Card>
  );
}
